import { Decimal } from './decimal';
import { formaterPourcentage } from './formatage';

/**
 * Règles du référentiel partagées entre l'API et l'interface (doc 01, § 3
 * et doc 02, § 14). Fonctions pures : elles servent à la fois à valider
 * côté serveur et à désactiver un bouton côté écran, sans divergence
 * possible entre les deux.
 */

export const TYPES_INSTRUMENT = ['ACTION', 'BDT', 'OPCVM'] as const;
export type TypeInstrument = (typeof TYPES_INSTRUMENT)[number];

export const STATUTS_INSTRUMENT = ['A_COMPLETER', 'ACTIF', 'SUSPENDU', 'RADIE'] as const;
export type StatutInstrument = (typeof STATUTS_INSTRUMENT)[number];

/** Nomenclature AMMC (doc 00, § 4). */
export const CATEGORIES_OPCVM = ['MONETAIRE', 'OCT', 'MT', 'MLT', 'LT'] as const;
export type CategorieOpcvm = (typeof CATEGORIES_OPCVM)[number];

export const LIBELLE_CATEGORIE_OPCVM: Record<CategorieOpcvm, string> = {
  MONETAIRE: 'Monétaire',
  OCT: 'Obligations court terme',
  MT: 'Moyen terme',
  MLT: 'Moyen et long terme',
  LT: 'Long terme',
};

export const POCHES = ['ACTIONS', 'TAUX', 'OPCVM'] as const;
export type Poche = (typeof POCHES)[number];

/* ── Tranches de maturité ────────────────────────────────────────────── */

export interface Tranche {
  id?: number;
  code: string;
  libelle: string;
  borneInfAns: number;
  borneSupAns: number;
  ordre: number;
}

export interface AnomalieTranches {
  code: 'BORNES_INVALIDES' | 'RECOUVREMENT' | 'DISCONTINUITE' | 'CODE_DUPLIQUE';
  message: string;
}

/**
 * Contrainte C-03 : bornes cohérentes, aucun recouvrement, aucune lacune.
 *
 * La continuité n'est pas un raffinement : une ligne BDT dont la maturité
 * résiduelle tomberait dans un trou ne serait rattachée à aucune ligne
 * d'allocation et deviendrait silencieusement « hors cible » (doc 01, § 6.2).
 */
export function validerTranches(tranches: readonly Tranche[]): AnomalieTranches[] {
  const anomalies: AnomalieTranches[] = [];
  if (tranches.length === 0) return anomalies;

  const codes = new Set<string>();
  for (const t of tranches) {
    if (codes.has(t.code)) {
      anomalies.push({ code: 'CODE_DUPLIQUE', message: `Code en double : ${t.code}.` });
    }
    codes.add(t.code);

    if (!(t.borneSupAns > t.borneInfAns)) {
      anomalies.push({
        code: 'BORNES_INVALIDES',
        message: `${t.libelle} : la borne supérieure doit dépasser la borne inférieure.`,
      });
    }
    if (t.borneInfAns < 0) {
      anomalies.push({
        code: 'BORNES_INVALIDES',
        message: `${t.libelle} : la borne inférieure ne peut être négative.`,
      });
    }
  }

  const triees = [...tranches].sort((a, b) => a.borneInfAns - b.borneInfAns);
  for (let i = 1; i < triees.length; i++) {
    const precedente = triees[i - 1]!;
    const courante = triees[i]!;
    if (courante.borneInfAns < precedente.borneSupAns) {
      anomalies.push({
        code: 'RECOUVREMENT',
        message: `${precedente.libelle} et ${courante.libelle} se recouvrent.`,
      });
    } else if (courante.borneInfAns > precedente.borneSupAns) {
      anomalies.push({
        code: 'DISCONTINUITE',
        message: `Lacune entre ${precedente.libelle} et ${courante.libelle} : une ligne BDT de cette maturité ne serait rattachée à aucune poche.`,
      });
    }
  }

  return anomalies;
}

/** Maturité résiduelle en années, base 365 (doc 02, § 13). */
export function maturiteResiduelleAns(dateEcheance: Date, dateReference: Date): number {
  const jours = (dateEcheance.getTime() - dateReference.getTime()) / 86_400_000;
  return jours / 365;
}

/**
 * Tranche d'une ligne BDT à une date donnée. Bornes : inférieure incluse,
 * supérieure exclue.
 *
 * Le rattachement est **recalculé à chaque date** : une ligne change de
 * tranche en vieillissant, et c'est voulu (doc 01, § 3.2).
 */
export function trancheDeMaturite<T extends Tranche>(
  dateEcheance: Date,
  dateReference: Date,
  tranches: readonly T[],
): T | null {
  const maturite = maturiteResiduelleAns(dateEcheance, dateReference);
  if (maturite < 0) return null; // ligne échue
  return tranches.find((t) => maturite >= t.borneInfAns && maturite < t.borneSupAns) ?? null;
}

/* ── Benchmark composite ─────────────────────────────────────────────── */

export interface ComposanteBenchmark {
  indiceCode: string;
  poidsPct: string | Decimal;
}

/** Contrainte C-02 : la somme des poids d'un benchmark vaut exactement 100 %. */
export function validerBenchmark(composantes: readonly ComposanteBenchmark[]): string | null {
  if (composantes.length === 0) return 'Un profil doit porter au moins un indice de référence.';

  const codes = new Set<string>();
  for (const c of composantes) {
    if (codes.has(c.indiceCode)) return `Indice en double : ${c.indiceCode}.`;
    codes.add(c.indiceCode);
    if (new Decimal(c.poidsPct.toString()).lessThanOrEqualTo(0)) {
      return `Le poids de ${c.indiceCode} doit être strictement positif.`;
    }
  }

  const total = composantes.reduce(
    (acc, c) => acc.plus(new Decimal(c.poidsPct.toString())),
    new Decimal(0),
  );
  if (!total.equals(100)) {
    // Message destiné à l'utilisateur : il suit le formatage français de la
    // charte, comme tout nombre affiché dans l'application.
    return `La somme des poids vaut ${formaterPourcentage(total)}, elle doit valoir exactement 100 %.`;
  }
  return null;
}

/* ── Instruments ─────────────────────────────────────────────────────── */

/** Un ISIN marocain : 2 lettres pays, 9 alphanumériques, 1 clé. */
export const MOTIF_ISIN = /^[A-Z]{2}[A-Z0-9]{9}\d$/;

export function isinValide(isin: string): boolean {
  return MOTIF_ISIN.test(isin);
}
